import * as THREE from 'three';

/**
 * 将车辆的航向角（heading）转换为Three.js的四元数（Quaternion）。
 * 航向角以度为单位，正北为0度，顺时针增加。
 * Three.js中，Y轴朝上，rotation.y 的正角度表示从Y轴上方看是逆时针旋转。
 *
 * @param heading 航向角 (0-360度)
 * @returns 代表该朝向的THREE.Quaternion
 */
export function headingToQuaternion(heading: number): THREE.Quaternion {
  // 1. 将航向角从度转换为弧度
  const headingRad = heading * (Math.PI / 180);

  // 2. Three.js的Y轴旋转是围绕Y轴的，正向为逆时针。
  //    而航向角是顺时针的。因此我们需要取反。
  // 3. 航向角0度（正北）被假定为对齐世界坐标的Z轴正方向。
  //    车辆模型默认朝向自身Z轴正方向。
  //    因此，只需将航向角取反即可得到正确的旋转角度。
  const angle = -headingRad;

  const quaternion = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0); // 围绕Y轴旋转
  quaternion.setFromAxisAngle(up, angle);
  return quaternion;
}

/**
 * 计算两个向量之间的带符号角度（弧度）。
 *
 * @param v1 起始向量
 * @param v2 结束向量
 * @param normal 用于确定旋转方向的法向量
 * @returns 两个向量间的角度，范围在 -PI 到 PI
 */
export function signedAngleTo(
  v1: THREE.Vector3,
  v2: THREE.Vector3,
  normal: THREE.Vector3 = new THREE.Vector3(0, 1, 0)
): number {
  const angle = v1.angleTo(v2);
  const cross = new THREE.Vector3().crossVectors(v1, v2);
  return cross.dot(normal) < 0 ? -angle : angle;
}

/**
 * 角度环绕处理，确保增量旋转走最短路径。
 *
 * @param currentAngle 当前角度（弧度）
 * @param targetAngle 目标角度（弧度）
 * @returns 修正后的目标角度
 */
export function unwrapAngle(currentAngle: number, targetAngle: number): number {
  const diff = targetAngle - currentAngle;
  if (Math.abs(diff) > Math.PI) {
    if (diff > 0) {
      return targetAngle - 2 * Math.PI;
    } else {
      return targetAngle + 2 * Math.PI;
    }
  }
  return targetAngle;
}

/**
 * 🚀 三次 Hermite 样条插值
 * 创建一条平滑的曲线，该曲线通过 p0 和 p1，
 * 并且在 p0 处的切线（速度）是 v0，在 p1 处的切线是 v1。
 *
 * @param p0 起始点 (THREE.Vector3)
 * @param v0 起始点的速度矢量 (THREE.Vector3)
 * @param p1 结束点 (THREE.Vector3)
 * @param v1 结束点的速度矢量 (THREE.Vector3)
 * @param t 插值因子 (0 到 1)
 * @param target 可选的输出向量
 * @returns 插值后的点 (THREE.Vector3)
 */
export function cubicHermiteSpline(
  p0: THREE.Vector3,
  v0: THREE.Vector3,
  p1: THREE.Vector3,
  v1: THREE.Vector3,
  t: number,
  target: THREE.Vector3 = new THREE.Vector3()
): THREE.Vector3 {
  const t2 = t * t;
  const t3 = t * t * t;

  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + t;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;

  target.x = h00 * p0.x + h10 * v0.x + h01 * p1.x + h11 * v1.x;
  target.y = h00 * p0.y + h10 * v0.y + h01 * p1.y + h11 * v1.y;
  target.z = h00 * p0.z + h10 * v0.z + h01 * p1.z + h11 * v1.z;

  return target;
}
